import { X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { cn } from '@/shared/lib/cn';

export interface SmartAutocompleteChangeMeta {
  source: 'existing' | 'temporary' | 'clear';
  is_temporary: boolean;
  pending_values: string[];
}

export interface SmartAutocompleteInputProps {
  available_values: readonly string[];
  value: string;
  onValueChange: (value: string, meta: SmartAutocompleteChangeMeta) => void;
  onPendingValuesChange?: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  allow_clear?: boolean;
  className?: string;
  inputClassName?: string;
  addActionLabel?: (value: string) => string;
  pendingBadgeLabel?: string;
}

interface OptionRow {
  label: string;
  normalized: string;
  source: 'existing' | 'temporary';
}

const ADD_ROW_ID = '__add_row__';

export function SmartAutocompleteInput(props: SmartAutocompleteInputProps): JSX.Element {
  const {
    available_values,
    value,
    onValueChange,
    onPendingValuesChange,
    placeholder,
    disabled,
    allow_clear = false,
    className,
    inputClassName,
    addActionLabel = (candidate) => `+ Agregar "${candidate}"`,
    pendingBadgeLabel = 'Nuevo (temporal)',
  } = props;

  const root_ref = useRef<HTMLDivElement | null>(null);
  const [input_value, setInputValue] = useState(value ?? '');
  const [is_open, setIsOpen] = useState(false);
  const [highlighted_index, setHighlightedIndex] = useState(-1);
  const [pending_values, setPendingValues] = useState<string[]>([]);

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!target) return;
      if (!root_ref.current?.contains(target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
    };
  }, []);

  const all_options = useMemo<OptionRow[]>(() => {
    const out = new Map<string, OptionRow>();
    for (const raw of available_values) {
      const label = cleanLabel(raw);
      if (!label) continue;
      const normalized = normalizeForSearch(label);
      if (!normalized || out.has(normalized)) continue;
      out.set(normalized, { label, normalized, source: 'existing' });
    }
    for (const raw of pending_values) {
      const label = cleanLabel(raw);
      if (!label) continue;
      const normalized = normalizeForSearch(label);
      if (!normalized || out.has(normalized)) continue;
      out.set(normalized, { label, normalized, source: 'temporary' });
    }
    return Array.from(out.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
    );
  }, [available_values, pending_values]);

  const normalized_query = normalizeForSearch(input_value);
  const filtered_options = useMemo(() => {
    if (!normalized_query) return all_options;
    return all_options.filter((option) => option.normalized.includes(normalized_query));
  }, [all_options, normalized_query]);

  const exact_match = useMemo(
    () => (normalized_query ? all_options.find((option) => option.normalized === normalized_query) : null),
    [all_options, normalized_query],
  );

  const can_add_value = Boolean(normalized_query && !exact_match);

  const list_rows = useMemo(
    () =>
      can_add_value
        ? [...filtered_options.map((option) => option.normalized), ADD_ROW_ID]
        : filtered_options.map((option) => option.normalized),
    [can_add_value, filtered_options],
  );

  const selected_option = useMemo(() => {
    const normalized_value = normalizeForSearch(value);
    if (!normalized_value) return null;
    return all_options.find((option) => option.normalized === normalized_value) ?? null;
  }, [all_options, value]);

  const emitPendingValues = (next: string[]): void => {
    setPendingValues(next);
    onPendingValuesChange?.(next);
  };

  const selectOption = (option: OptionRow): void => {
    setInputValue(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onValueChange(option.label, {
      source: option.source,
      is_temporary: option.source === 'temporary',
      pending_values,
    });
  };

  const addTemporaryValue = (): void => {
    const candidate = cleanLabel(input_value);
    if (!candidate) return;
    const normalized = normalizeForSearch(candidate);
    if (!normalized) return;

    const existing = all_options.find((option) => option.normalized === normalized);
    if (existing) {
      selectOption(existing);
      return;
    }

    const next_pending = [...pending_values, candidate];
    emitPendingValues(next_pending);
    setInputValue(candidate);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onValueChange(candidate, {
      source: 'temporary',
      is_temporary: true,
      pending_values: next_pending,
    });
  };

  const clearSelection = (): void => {
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onValueChange('', { source: 'clear', is_temporary: false, pending_values });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!is_open) setIsOpen(true);
      if (list_rows.length === 0) return;
      setHighlightedIndex((prev) => (prev + 1 >= list_rows.length ? 0 : prev + 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!is_open) setIsOpen(true);
      if (list_rows.length === 0) return;
      setHighlightedIndex((prev) => (prev <= 0 ? list_rows.length - 1 : prev - 1));
      return;
    }

    if (event.key !== 'Enter') return;
    event.preventDefault();

    if (highlighted_index >= 0 && highlighted_index < list_rows.length) {
      const row_id = list_rows[highlighted_index];
      if (row_id === ADD_ROW_ID) {
        addTemporaryValue();
        return;
      }
      const option = all_options.find((item) => item.normalized === row_id);
      if (option) {
        selectOption(option);
        return;
      }
    }

    if (exact_match) {
      selectOption(exact_match);
      return;
    }

    if (can_add_value) {
      addTemporaryValue();
    }
  };

  const show_dropdown = is_open && (filtered_options.length > 0 || can_add_value);

  return (
    <div ref={root_ref} className={cn('relative overflow-visible', className)}>
      <div className="relative">
        <input
          type="text"
          value={input_value}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => {
            setIsOpen(true);
          }}
          onChange={(event) => {
            setInputValue(event.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onKeyDown={onKeyDown}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            inputClassName,
          )}
        />
        {allow_clear && !disabled && value.trim().length > 0 ? (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Limpiar"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>

      {show_dropdown ? (
        <div className="absolute left-0 top-full z-[90] mt-2 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-background p-1 shadow-xl ring-1 ring-border">
          {filtered_options.map((option) => {
            const row_index = list_rows.indexOf(option.normalized);
            const highlighted = row_index === highlighted_index;
            return (
              <button
                key={option.normalized}
                type="button"
                className={cn(
                  'flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm',
                  highlighted ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
                )}
                onMouseEnter={() => setHighlightedIndex(row_index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectOption(option);
                }}
              >
                <span>{option.label}</span>
                {option.source === 'temporary' ? (
                  <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                    {pendingBadgeLabel}
                  </span>
                ) : null}
              </button>
            );
          })}

          {can_add_value ? (
            <button
              type="button"
              className={cn(
                'flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm font-medium text-primary hover:bg-accent/60',
                list_rows[highlighted_index] === ADD_ROW_ID ? 'bg-accent text-accent-foreground' : '',
              )}
              onMouseEnter={() => setHighlightedIndex(list_rows.indexOf(ADD_ROW_ID))}
              onMouseDown={(event) => {
                event.preventDefault();
                addTemporaryValue();
              }}
            >
              <span>{addActionLabel(cleanLabel(input_value) ?? input_value.trim())}</span>
              <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                {pendingBadgeLabel}
              </span>
            </button>
          ) : null}
        </div>
      ) : null}

      {selected_option?.source === 'temporary' ? (
        <p className="mt-1 text-xs text-emerald-700">{pendingBadgeLabel}</p>
      ) : null}
    </div>
  );
}

function cleanLabel(input: string | null | undefined): string | null {
  if (!input) return null;
  const collapsed = input.trim().replace(/\s+/g, ' ');
  return collapsed.length > 0 ? collapsed : null;
}

function normalizeForSearch(input: string | null | undefined): string {
  if (!input) return '';
  return input
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}
